import { useState, useEffect } from "react";
import axios from "axios";
import { Activity, Package, Shield, Users, FileText, KeyRound, Download } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_ICONS = {
  CREATE_INVENTORY: Package,
  DELETE_INVENTORY: Package,
  UPLOAD_FILE: Download,
  CHANGE_USER_ROLE: Users,
  UPDATE_USER_PERMISSIONS: Shield,
  RESET_USER_PERMISSIONS: Shield,
  PASSWORD_CHANGED: KeyRound,
  "2FA_ENFORCEMENT_CHANGED": Shield,
  LOGIN_SUCCESS: Activity,
  OTP_VERIFICATION_SUCCESS: Shield,
  PASSWORD_RESET_REQUESTED: KeyRound,
};

const ACTION_COLORS = {
  CREATE_INVENTORY: "text-green-600 bg-green-50",
  DELETE_INVENTORY: "text-red-600 bg-red-50",
  UPLOAD_FILE: "text-blue-600 bg-blue-50",
  CHANGE_USER_ROLE: "text-purple-600 bg-purple-50",
  UPDATE_USER_PERMISSIONS: "text-orange-600 bg-orange-50",
  RESET_USER_PERMISSIONS: "text-orange-600 bg-orange-50",
  PASSWORD_CHANGED: "text-amber-600 bg-amber-50",
  LOGIN_SUCCESS: "text-slate-600 bg-slate-50",
  OTP_VERIFICATION_SUCCESS: "text-green-600 bg-green-50",
  PASSWORD_RESET_REQUESTED: "text-amber-600 bg-amber-50",
};

const timeAgo = (timestamp) => {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const ActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivity();
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/activity-feed?limit=12`);
      setActivities(response.data);
    } catch (error) {
      console.error("Failed to fetch activity feed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
            Recent Activity
          </h2>
        </div>
        <div className="p-8 text-center text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="activity-feed">
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900 uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
              Recent Activity
            </h2>
            <p className="text-xs text-slate-500 mt-1">Latest actions across the system</p>
          </div>
          <Activity className="text-slate-400" size={22} />
        </div>
      </div>
      <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
        {activities.length === 0 ? (
          <div className="p-8 text-center">
            <Activity className="mx-auto text-slate-300 mb-3" size={40} />
            <p className="text-slate-500">No recent activity</p>
          </div>
        ) : (
          activities.map((item) => {
            const Icon = ACTION_ICONS[item.action] || FileText;
            const colorClass = ACTION_COLORS[item.action] || "text-slate-600 bg-slate-50";
            const emailUser = item.user_email?.split("@")[0] || "system";

            return (
              <div
                key={item.id}
                className="flex items-start gap-3 px-5 py-3.5 hover:bg-slate-50/60 transition-colors"
                data-testid={`activity-item-${item.id}`}
              >
                <div className={`p-1.5 rounded-sm flex-shrink-0 mt-0.5 ${colorClass}`}>
                  <Icon size={14} strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 leading-snug">
                    <span className="font-semibold">{emailUser}</span>{" "}
                    <span className="text-slate-600">{item.description}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{timeAgo(item.timestamp)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export { ActivityFeed };
